import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { TopBar, DestinationRow, ActionButton, QuoteEchoCard, FieldError } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import api from "../lib/api";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import { track } from "../lib/usageTracker";

/**
 * Issue #763 — "the plan, one sentence", wave 4's opening slice. Behind
 * `newUi("plan")`, replaces the WHOLE `/development` page (same "the
 * rendered screen replaces the page" mechanism #757/#760 use for
 * `/performance`/`/dashboard`) for a player with no goal, or on a `?new=1`
 * deep link — `pages/DevelopmentPlan.jsx`'s own `computePlanGate`
 * (`lib/planGate.js`) decides which. Matches `Plan.dc.html` (round 5,
 * sha256 17495ae5…) — the approved drawing is the contract, ruled against
 * the issue text in brief-763.md where they differ.
 *
 * D98 (Andreas, 2026-09-24) supersedes the issue's own §5: there is NO
 * "rest of the plan" disclosure on this screen. Horizon, category, due
 * date, status, actions and notes stay in the OLD dialog
 * (`DevelopmentPlan.jsx`'s `NewGoalDialog`), reachable once a plan already
 * exists — this screen never renders them. The `POST /goals` body below is
 * byte-identical to that dialog's own defaults
 * (`DevelopmentPlan.jsx` ~:496) for every field this screen does not set —
 * only `target`/`goal_title` ever differs from them.
 *
 * D109: no success toast fires here — the screen's own gate
 * (`showNewPlan`, driven by the open-goal count) flips false once
 * `onCreated`'s reload lands a non-empty list, and the player lands on
 * today's goals view because THAT is what "the screen itself confirms"
 * means for this slice. D55/D57: both error states below render ON THE
 * PAGE, in ink, never as a toast — the field error is the
 * 2px alert-red `--nw-border-invalid` border plus the kit `FieldError`
 * glyph (D303), with the sentence in ink, and the save-failed banner
 * mirrors `LogMatchScorecardStep.jsx`'s own (unexported) `ErrorBanner`
 * style token-for-token rather than importing a private, unexported
 * helper.
 *
 * D92 (decisions-2026-09-24.md) — this screen is player-only, same as
 * `RecordEmptyScreen.jsx`: `DestinationRow` "every caller of this
 * component is player-only" is enforced one level up, in
 * `DevelopmentPlan.jsx`'s own `isPlayer` gate (`lib/planGate.js`) — this
 * component never mounts for a coach or an admin, so it carries no role
 * check of its own.
 *
 * `onCurrentDestinationTap` — fix round, F1 (scoped by RR2, second fix
 * round). Optional; wired to `kit/DestinationRow.jsx`'s own
 * `onCurrentClick` on the "Plan" chip. A player who already has a goal and
 * arrived via `?new=1` had no way off this screen except browser Back (the
 * gate's own `createOpen` wins outright, and nothing else cleared it);
 * tapping the now-current "Plan" chip fires this instead, which
 * `DevelopmentPlan.jsx` uses to clear that intent so its gate can re-settle
 * on its own next render. `DevelopmentPlan.jsx` passes this ONLY when
 * there is an actual create intent to clear (RR2) — a genuine zero-goal
 * player reaches this screen through the auto-swap branch instead, where
 * tapping "Plan" would do nothing, so the chip stays a plain, inert
 * `<span>` for them (no `role="button"`, no cursor, nothing to tap).
 */
export default function PlanScreen({ onCreated, onCurrentDestinationTap, comingUp = null }) {
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  const [target, setTarget] = useState("");
  const [targetError, setTargetError] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "failed"

  // Same mount contract `RecordEmptyScreen.jsx`/`LogGroundFlow.jsx` use:
  // fires once for the whole mounted lifetime of this screen (not once per
  // internal state change), so the chrome it hides/shows never flickers
  // while the player types or saves.
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = target.trim();

  // #1308 (D303): Save takes the player to the missing sentence. `attempt`
  // counts failed Saves so a second empty Save is a new error set.
  const fieldRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(() => ({ target: targetError ? attempt : 0 }), [targetError, attempt]);
  const armFocus = useFocusFirstInvalid(fieldRef, focusErrors);

  const save = async () => {
    armFocus();
    if (!trimmed) {
      // D55/D57 — no toast. The field itself carries the error.
      setTargetError(true);
      setAttempt((n) => n + 1);
      return;
    }
    setTargetError(false);
    setSaveState("saving");
    // D98 — byte-identical to `NewGoalDialog`'s own defaults
    // (`DevelopmentPlan.jsx` ~:496/~:500-511) for every held field; only
    // `target`/`goal_title` ever comes from this screen.
    const payload = {
      goal_title: target,
      target,
      description: "",
      target_date: null,
      progress_percentage: 0,
      status: "not_started",
      horizon: "short",
      category: "general",
      notes: "",
      milestones: [],
    };
    try {
      await api.post("/goals", payload);
      track("plan.goal_create");
      // No success toast (D109) — `onCreated` reloads the goals list and
      // clears `DevelopmentPlan.jsx`'s own create-intent state; once the
      // reload lands, that page's gate turns false and this component
      // unmounts in favour of today's goals view. The two lines below are
      // a safety net, not the normal path: if the gate somehow stays true
      // (e.g. the reload itself failed open), the form resets to idle
      // rather than getting stuck showing "Saving…" forever.
      await onCreated();
      setSaveState("idle");
      setTarget("");
    } catch {
      setSaveState("failed");
    }
  };

  return (
    <div data-testid="plan" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="white"
        // PR #887 review, B4 — `topbar-logo` + the home link `Layout.jsx`'s
        // own header carries everywhere else; this screen is player-only,
        // so its home is always `/dashboard`, the same reasoning
        // `RecordEmptyScreen.jsx` follows.
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        // D26 (decisions-2026-09-24.md) — every navy-TopBar artboard in
        // waves 2-6 carries the contrast trigger "using the same
        // transparent, white-icon rule as the #834 fix", the same ruling
        // `RecordEmptyScreen.jsx` follows; `plan-topbar` joins that rule in
        // `index.css`. `hideTextSize` swaps the "Aa" trigger for the
        // `Contrast` glyph (issue #797) — this screen's own type ramp is
        // fixed px (the kit's `.nw-*` classes), so the text-size control
        // moves nothing here either.
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu hideTextSize surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="plan-topbar"
      />
      <DestinationRow current="plan" onCurrentClick={onCurrentDestinationTap} />

      {/* Plan.dc.html:71 draws `gap: 14px` for this stack — `--nw-space-stack`
          (index.css), the same 14px rhythm SetUp.dc.html/CoachSetUp.dc.html/
          CoachPortalEmpty.dc.html/FirstGround.dc.html all draw. NOT
          `--nw-space-log-stack` (16px) — fix round: that token is
          `DirectionA1.dc.html`'s own drawn number for the log-a-match
          family (index.css's own comment on it), a different screen with a
          different rhythm; a first pass here borrowed it by mistake. */}
      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "24px 20px 20px" }}>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div className="nw-eyebrow">Your plan</div>
          <div className="nw-title-lg">What are you working on this season?</div>
          <div className="nw-lede">
            One thing, in your own words — the way you'd say it to your captain in the car park. No list, no
            categories.
          </div>
        </div>

        {/* Issue #1149 — Coming up (and its Add menu) above the one sentence,
            for a player with no goal yet who may already have planned a match
            or a session. `null` flag-off: the screen is then byte-identical. */}
        {comingUp}

        <div ref={fieldRef} className="flex flex-col" style={{ gap: 5 }}>
          <div className="nw-label">Target</div>
          {/* Plain wrapper: the column's gap-5 would add to FieldError's own
              4px margin; the board (D259) draws 4px between field and sentence. */}
          <div>
            <textarea
              id="plan-target"
              data-testid="goal-target"
              aria-invalid={targetError ? "true" : undefined}
              aria-describedby={targetError ? "plan-target-error" : undefined}
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                // D55 — the error clears the moment the player starts fixing
                // it, same as every other required field on this app; it
                // does not wait for another submit press.
                if (targetError) setTargetError(false);
              }}
              placeholder="Type it here — one sentence is plenty."
              rows={4}
              className="nw-kit-input nw-field"
              style={{
                // A textarea is inline-level: inside the plain wrapper it would leave
                // a line-box strip under itself and push the sentence past 4px.
                display: "block",
                width: "100%",
                minHeight: 112,
                padding: 14,
                background: "var(--nw-surface)",
                border: targetError ? "var(--nw-border-invalid)" : "1px solid var(--nw-navy)",
                borderRadius: "var(--nw-radius-card)",
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                fontSize: 15,
                lineHeight: 1.5,
                color: "var(--nw-ink)",
                resize: "none",
                // The focus ring is `.nw-field` in index.css (#1334): the browser
                // ring is off so it does not cover the red border, and a navy or
                // alert ring replaces it on keyboard focus.
              }}
            />
            {targetError && (
              <FieldError id="plan-target-error" testId="goal-target-error">
                Target is required
              </FieldError>
            )}
          </div>
        </div>

        {/* "What you told us" — render nothing while the field is empty
            (issue text §4 / brief copy block). Live, on every keystroke,
            not on blur — `target` is the same state the textarea above is
            controlled by, so there is nothing to debounce.

            #868/#872 fix round — extracted into the kit's own
            `QuoteEchoCard` (`components/kit/QuoteEchoCard.jsx`): set-up
            step 2 draws the identical card shape at a different gap (6px)
            with a different helper line, and a second literal copy of this
            markup was the wrong way to let it. `plan-quote` and this
            screen's own helper stay exactly as they were — only the shape
            moved. */}
        {trimmed && (
          <QuoteEchoCard
            quote={trimmed}
            testId="plan-quote"
            helper="That's your sentence, back before anything else happens. We don't score it, rename it, or decide what kind of cricketer it makes you."
          />
        )}

        <div style={{ flexGrow: 1 }} />

        {saveState === "failed" && (
          <>
            {/* Mirrors `LogMatchScorecardStep.jsx`'s own (unexported)
                `ErrorBanner` style token-for-token — D57: on the page, in
                ink, never a toast. */}
            <div
              data-testid="goal-save-error"
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--nw-ink)",
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                borderLeft: "3px solid var(--nw-alert)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              Failed to save
            </div>
            <div className="nw-help">Your sentence is still here. Try again.</div>
          </>
        )}

        <ActionButton
          data-testid="goal-create-btn"
          onClick={save}
          disabled={saveState === "saving"}
          // PR #841 review, B4's own reasoning (LogMatchScorecardStep.jsx)
          // — `Plan.dc.html:100` draws the saving state at full gold,
          // still disabled (nothing double-submits), just not dimmed.
          icon={saveState === "saving" ? <Loader2 size={18} style={{ color: "#0F172A" }} className="animate-spin" /> : null}
          style={saveState === "saving" ? { color: "#0F172A", opacity: 1 } : { color: "#0F172A" }}
        >
          {saveState === "saving" ? "Saving…" : "Make it a plan"}
        </ActionButton>
      </div>
    </div>
  );
}
